/**
 * 비로그인 상태의 Pick — 이 브라우저에만 있다.
 *
 * 서비스의 "Try First, Login Later" 원칙이 여기 걸려 있다. 로그인 전까지는
 * 서버를 전혀 쓰지 않고, 로그인할 때 이 목록을 계정으로 옮긴다.
 *
 * **사진 주소가 없다.** 사진은 필수지만 업로드에는 로그인이 필요하므로, 로그인
 * 전에는 사진 자체를 IndexedDB에 두고(`imageStore.ts`) 여기에는 그게 있다는
 * 사실만 둔다. 이관할 때 올리고 나서야 진짜 주소가 생긴다.
 *
 * ⚠️ **저장된 값을 믿지 않는다.** 사용자가 직접 고칠 수 있고, 예전 버전이
 * 남아 있을 수 있다. 읽을 때마다 형태를 검증하고, 깨진 항목은 버린다 —
 * 깨진 데이터 하나로 목록 전체가 안 보이면 안 된다.
 */

import { z } from 'zod';

import { categoryIdSchema } from '@lifetimeline/shared';

const STORAGE_KEY = 'lifetimeline.localPicks';

/** 저장 한도. 무한정 쌓이면 이관 요청이 서버 한도(100)를 넘는다. */
export const LOCAL_PICK_LIMIT = 100;

/**
 * 로컬 전용 Pick.
 *
 * `hasImage`가 false인 항목은 **사진 필수가 되기 전에** 저장된 것이다. 지우지
 * 않고 그대로 둔다(사용자 데이터다) — 수정할 때 사진을 넣게 하고, 그 전까지는
 * 격자에서 글자 칸으로 보인다.
 */
export const localPickSchema = z.object({
  categoryId: categoryIdSchema,
  itemName: z.string().trim().min(1).max(100),
  itemDescription: z.string().trim().max(200).optional(),
  /** 사진이 IndexedDB에 있는지. 실제 Blob은 categoryId를 키로 따로 저장된다 */
  hasImage: z.boolean().default(false),
});
export type LocalPick = z.infer<typeof localPickSchema>;

function readRaw(): unknown {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? null : JSON.parse(stored);
  } catch {
    // 시크릿 창, 저장소 차단, 깨진 JSON
    return null;
  }
}

/**
 * 저장된 목록을 읽는다. 형태가 맞지 않는 항목은 조용히 버린다.
 *
 * 전체를 버리지 않는 이유: 하나가 깨졌다고 나머지 선택까지 잃게 하면
 * 사용자 입장에서는 그냥 데이터가 사라진 것이다.
 */
export function readLocalPicks(): LocalPick[] {
  const raw = readRaw();
  if (!Array.isArray(raw)) {
    return [];
  }

  const valid: LocalPick[] = [];
  const seen = new Set<string>();

  for (const item of raw) {
    const parsed = localPickSchema.safeParse(item);
    if (!parsed.success) {
      continue;
    }
    // 같은 카테고리가 두 번 있으면 뒤엣것만 남긴다 — 서비스 원칙이 "하나"다
    if (seen.has(parsed.data.categoryId)) {
      continue;
    }
    seen.add(parsed.data.categoryId);
    valid.push(parsed.data);
  }

  return valid;
}

function writeLocalPicks(picks: LocalPick[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(picks));
  } catch {
    // 저장소가 가득 찼거나 차단됨. 이번 세션 화면에는 이미 반영돼 있다.
  }
}

/**
 * 하나를 넣는다. 같은 카테고리가 있으면 **교체한다** — 서비스 제1원칙이
 * 서버뿐 아니라 로컬에서도 같아야 한다.
 */
export function putLocalPick(pick: LocalPick): LocalPick[] {
  const current = readLocalPicks().filter((item) => item.categoryId !== pick.categoryId);

  if (current.length >= LOCAL_PICK_LIMIT) {
    // 한도를 넘으면 가장 오래된 것을 밀어낸다. 새로 고른 것을 거부하면
    // 사용자는 왜 저장이 안 되는지 알 수 없다.
    current.shift();
  }

  const next = [...current, pick];
  writeLocalPicks(next);
  return next;
}

/**
 * 순서를 바꾼다. **배열 순서가 곧 화면 순서다.**
 *
 * 로그인 목록처럼 `position`을 따로 두지 않는 이유: 여기는 배열 하나를 통째로
 * 읽고 쓰므로 순서를 담을 곳이 이미 있다. 번호를 더 두면 배열 순서와 번호가
 * 어긋날 수 있고, 어느 쪽이 맞는지 판단할 근거가 없다.
 *
 * 목록에 없는 id가 섞여 오면 무시하고, 빠진 것은 뒤에 붙인다 — 화면이 잠깐
 * 어긋나 있어도 Pick이 사라지지는 않게.
 */
export function reorderLocalPicks(categoryIds: readonly string[]): LocalPick[] {
  const current = readLocalPicks();
  const byId = new Map(current.map((pick) => [pick.categoryId, pick]));

  const ordered: LocalPick[] = [];
  for (const categoryId of categoryIds) {
    const pick = byId.get(categoryId);
    if (pick !== undefined) {
      ordered.push(pick);
      byId.delete(categoryId);
    }
  }
  const next = [...ordered, ...byId.values()];

  writeLocalPicks(next);
  return next;
}

export function removeLocalPick(categoryId: string): LocalPick[] {
  const next = readLocalPicks().filter((item) => item.categoryId !== categoryId);
  writeLocalPicks(next);
  return next;
}

export function findLocalPick(categoryId: string): LocalPick | undefined {
  return readLocalPicks().find((item) => item.categoryId === categoryId);
}

/**
 * 목록을 비운다. **계정으로 이관이 끝난 뒤에만** 부른다 —
 * 서버 저장이 성공하기 전에 지우면 데이터가 증발한다.
 */
export function clearLocalPicks(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 무시
  }
}

export function hasLocalPicks(): boolean {
  return readLocalPicks().length > 0;
}
